import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Context } from "../../../../context";
import { Box, CircularProgress, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { Layout } from "../../../../containers/layout/dashboard";
import { AccessControlWrapper } from "./styled";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Menu } from "../../../../components/menu";
import { MenuItemType } from "../../../../types/app.type";
import { BaseButton } from "../../../../components/button/styled";
import Cookies from "universal-cookie";
import { updateAccessControlService } from "../../../../utils/apis/usermanagement/accesscontrol/updateAccessControl";
import { BaseAlertModal } from "../../../../components/modals/alert";
import { retrieveAccessControlByIdService } from "../../../../utils/apis/usermanagement/accesscontrol/retrieveAccessControlById";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { retrieveAllModulesService } from "../../../../config/modules";
import { useQuery } from "@tanstack/react-query";
import { BaseFieldSet } from "../../../../components/formfields/fieldset/styled";
import { BaseOption } from "../../../../components/formfields/option/styled";
import { BaseSelect } from "../../../../components/formfields/select/styled";
import { retrieveAllRolesService } from "../../../../utils/apis/usermanagement/role/retrieveAllRoles";
import { retrieveAllSystemPermissionsService } from "../../../../config/systemPermissions";

export const AccessControl = () => {
    const voidRole = "MFI Admin";
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;
    const initialPermissions = useMemo(() => [
        { module: "Dashboard", rights: [] },
        { module: "Institution", rights: [] },
        { module: "Accounting", rights: [] },
        { module: "User Management", rights: [] },
        { module: "Configuration", rights: [] },
        { module: "Report", rights: [] },
        { module: "Migration", rights: [] },
    ], []);

    const navigate = useNavigate();
    const { id = undefined } = useParams();
    const { setActiveNavItem } = useContext(Context);

    const [openAlertModal, setOpenAlertModal] = useState(false);
    const [activeModule, setActiveModule] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [accessControlId, setAccessControlId] = useState<string>(" ");
    // if you ever deep it, this initial state of the permissions state solely 
    // exists to enable us control (i.e set) permissions update. Asides this, the initial 
    // values of permissions is worthless. But now, you know you should leave it alone.
    const [permissions, setPermissions] = useState<MenuItemType[]>(initialPermissions);

    useEffect(() => {
        setActiveNavItem("User Management");
    }, [setActiveNavItem]);

    const { data: systemPermissions } = useQuery({
        queryKey: ['systemPermissions', TOKEN],
        queryFn: async () => retrieveAllSystemPermissionsService(TOKEN),
        enabled: !!TOKEN,
    });

    const { data: modules } = useQuery({
        queryKey: ['modules', TOKEN],
        queryFn: async () => retrieveAllModulesService(TOKEN),
        enabled: !!TOKEN,
    });

    const { data: roles } = useQuery({
        queryKey: ['roles', TOKEN],
        queryFn: async () => {
            const allRoles = await retrieveAllRolesService("", TOKEN);
            return allRoles.filter((role: { title: string }) => role.title !== voidRole);
        },
        enabled: !!TOKEN,
    });

    const fetchPermissions = useCallback(
        async (id: string) => {
            if (!id.trim()) {
                return setPermissions(initialPermissions);
            }
            try {
                const data = await retrieveAccessControlByIdService(TOKEN, id);
                setPermissions(data.permissions);
            } catch (err) {
                console.error("Failed to fetch access control:", err);
            }
        },
        [TOKEN, initialPermissions]
    );

    useEffect(() => {
        if (id) {
            fetchPermissions(id);
            setAccessControlId(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleRoleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { value } = e.target;
        setError(null);
        await fetchPermissions(value as string);
        return setAccessControlId(value as string);
    }

    const handleModuleClick = (e: React.MouseEvent<SVGSVGElement, MouseEvent> | React.MouseEvent<HTMLDivElement, MouseEvent>, module: string) => {
        e.stopPropagation();
        if (module === activeModule) {
            return setActiveModule(null);
        }
        return setActiveModule(module);
    };

    const handleAlertModalPersist = () => {
        return setOpenAlertModal(true);
    };

    const handleNavigateToRoles = () => {
        navigate("/usermanagement/userrole");
        return setOpenAlertModal(false);
    };

    const handleNavigateToPrevious = () => {
        return navigate(-1);
    };

    const handleCheckboxChange = (module: string, name: string) => {
        setPermissions((prevPermissions) =>
            prevPermissions.map((permission: Record<string, any>) =>
                permission.module === module
                    ? {
                        ...permission,
                        rights: permission.rights.some((right: Record<string, any>) => right.title === name)
                            ? permission.rights.filter((right: Record<string, any>) => right.title !== name) // Remove if exists
                            : [...permission.rights, { title: name }], // Add if not present
                    }
                    : permission
            )
        );
    };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        const payload = {
            permissions,
        };
        setError(null);
        setIsLoading(true);
        if (!accessControlId.trim()) {
            setIsLoading(false);
            return setError("Please select a role");
        };
        try {
            const response = await updateAccessControlService(TOKEN, accessControlId, payload);
            if (response.status === "success") {
                setIsLoading(false);
                setOpenAlertModal(true);
            } else {
                setIsLoading(false);
                setError('Access Control Update failed. Please try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Error: ${error.message}`);
            console.error('Error:', error);
        }
    };

    return (
        <Layout
            id="access-control"
        >
            <AccessControlWrapper>
                <BaseAlertModal
                    title="Success"
                    message="Access control successfully updated"
                    open={openAlertModal}
                    handleClose={handleAlertModalPersist}
                    callToAction="Continue"
                    handleCallToAction={handleNavigateToRoles}
                />
                <Stack
                    direction={{ tablet: "row" }}
                    gap={"var(--flex-gap)"}
                    justifyContent={"space-between"}
                    marginBlockEnd={"var(--basic-margin)"}
                >
                    <Box
                        component={"div"}
                        className={"title-box"}
                    >
                        <Typography
                            variant="h1"
                            fontFamily={"Inter"}
                            fontWeight={700}
                            fontSize={24}
                            lineHeight={"normal"}
                            color={"#373737"}
                            marginBlockEnd={"calc(var(--basic-margin)/4)"}
                        >
                            Setup Access Control
                        </Typography>
                        <BaseButton
                            variant="outlined"
                            border="1px solid #D7D7D7"
                            colour="#373737"
                            bgcolor="#FFFFFF"
                            padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                            fontSize={12}
                            fontWeight={800}
                            startIcon={<ArrowLeftIcon />}
                            onClick={handleNavigateToPrevious}
                        >
                            <Typography
                                variant={"button"}
                                fontFamily={"inherit"}
                                fontWeight={"inherit"}
                                fontSize={"inherit"}
                                lineHeight={"inherit"}
                                color={"inherit"}
                                textTransform={"inherit"}
                            >
                                Back
                            </Typography>
                        </BaseButton>
                    </Box>
                    <form>
                        <BaseFieldSet>
                            <BaseSelect
                                name="role"
                                colour="#666666"
                                bgcolor="#FFFFFF"
                                fontWeight={400}
                                fullWidth
                                value={accessControlId}
                                onChange={(e) => handleRoleChange(e)}
                            >
                                <BaseOption
                                    value={" "}
                                    colour="#666666"
                                    fontWeight={400}
                                >
                                    Select role
                                </BaseOption>
                                {roles?.map((role: Record<any, any>, index: string) => {
                                    return (
                                        <BaseOption
                                            key={index}
                                            value={role.accessControl}
                                            colour="#666666"
                                            fontWeight={400}
                                        >
                                            {role.title}
                                        </BaseOption>
                                    )
                                })}
                            </BaseSelect>
                        </BaseFieldSet>
                    </form>
                </Stack>
                <Box>
                    {modules?.map((module: string, index: string) => {
                        return (
                            <Box
                                key={index}
                                component={"div"}
                                className="module"
                            >
                                <Stack
                                    className="module-header"
                                    direction={"row"}
                                    alignItems={"center"}
                                    justifyContent={"space-between"}
                                    gap={"var(--flex-gap)"}
                                    onClick={(e) => handleModuleClick(e, module)}
                                >
                                    <Typography
                                        variant="h3"
                                        fontFamily={"Inter"}
                                        fontWeight={700}
                                        fontSize={20}
                                        lineHeight={"normal"}
                                        color={"#791377"}
                                    >
                                        {module}
                                    </Typography>
                                    <PlayArrowIcon
                                        onClick={(e) => handleModuleClick(e, module)}
                                    />
                                </Stack>
                                <Box>
                                    <Menu
                                        location="access-control"
                                        open={activeModule === module}
                                        activeModule={activeModule as string}
                                        permissions={permissions}
                                        menuitems={systemPermissions}
                                        handleCheckboxChange={handleCheckboxChange}
                                    />
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
                {error && <Typography
                    fontFamily={"Inter"}
                    fontWeight={"600"}
                    fontSize={16}
                    lineHeight={"normal"}
                    color={"#FF0000"}
                    whiteSpace={"normal"}
                    marginBlockStart={"var(--basic-margin)"}
                >
                    {error}
                </Typography>}
                <Box
                    component={"div"}
                    className="button-box"
                >
                    <BaseButton
                        variant="contained"
                        radius="7px"
                        fontSize={16}
                        fontWeight={800}
                        bgcolor="#791377"
                        padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
                        onClick={handleSubmit}
                    >
                        {isLoading ? (
                            <CircularProgress color="inherit" className="loader" />
                        ) : (
                            <Typography
                                variant={"button"}
                                fontFamily={"inherit"}
                                fontWeight={"inherit"}
                                fontSize={"inherit"}
                                lineHeight={"inherit"}
                                color={"inherit"}
                                textTransform={"inherit"}
                            >
                                Update
                            </Typography>
                        )}
                    </BaseButton>
                </Box>
            </AccessControlWrapper>
        </Layout >
    )
}
